import type { ReactNode } from 'react'
import React from 'react'

import { SYMBOL_MAP } from './symbols'

/**
 * Accepts a string and returns a string with all inline superscript indicators replaced with <sup> tags
 * E.g. hello world^2 becomes hello world<sup>2</sup>
 */
export const replaceForSuperScript = (text: string) => {
    return text.replace(
        /(\^\d+)/g,
        (i, match) => `<sup>${match.replace('^', '')}</sup>`,
    )
}

export const replaceForSymbol = (text: string): string => {
    let result = text

    Object.entries(SYMBOL_MAP).forEach(([unicode, symbol]) => {
        const { htmlCode, className } = symbol

        // Replace normal symbols
        const symbolClass = className ? `${className}` : ''
        result = result.replaceAll(
            `~symU+${unicode}`,
            `<span class="symbol ${symbolClass}">&#${htmlCode};</span>`,
        )

        // Replace superscript symbols
        result = result.replaceAll(
            `~supsymU+${unicode}`,
            `<sup>&#${htmlCode};</sup>`,
        )
    })

    return result
}

export const replaceForDisclaimer = (text: string): string => {
    const regex = /~disclaimer:/g
    const matches = text.match(regex)

    if (matches) {
        matches.forEach((match) => {
            const content = match.replace(/~disclaimer:/g, '')
            text = text.replace(match, `${content}`)
        })
    }

    return text
}

/**
 * HTML string replacement for dangerouslySetInnerHTML contexts (Markdown component).
 * Replaces ~ready:text~ with a span using the Ready brush font.
 * E.g. ~ready:GENERATION~ becomes <span class="font-ready">GENERATION</span>
 */
export const replaceForReadyFont = (text: string): string => {
    return text.replace(
        /~ready:([^~]+)~/gi,
        (_, content) => `<span class="font-ready">${content}</span>`,
    )
}

/**
 * React element replacement for JSX contexts (hero-v2, footer-cta).
 * Splits text at ~ready: markers and returns ReactNode[] with marked text
 * wrapped in <span className="font-ready">.
 * For text without markers, returns the original string unchanged.
 */
export const renderWithReadyFont = (text: string): ReactNode => {
    if (!text.match(/~ready:/i)) {
        return text
    }

    const parts = text.split(/(~ready:[^~]+~)/gi)
    return parts.map((part, i) => {
        const match = part.match(/~ready:([^~]+)~/i)
        if (match) {
            return React.createElement(
                'span',
                { key: i, className: 'font-ready' },
                match[1],
            )
        }
        return part
    })
}
